import { useState } from "react";
import Modal from "src/components/UI/Modal";
import {
  useGetLocksListQuery,
  useUnlockLockMutation,
} from "src/services/inventory/inventory";
import CustomSnackbar from "src/components/UI/Alerts/customSnackbar";
import { Pagination, Spinner } from "@heroui/react";
import { InventoryList } from "src/components/Inventory/inventoryList";
import { InventoryCard } from "src/components/Inventory/inventoryCard";
import type { InventoryItem } from "src/utils/interfaces/interfaces";
import { useTranslation } from "react-i18next";
import {
  FaBorderAll,
  FaListUl,
  FaLockOpen,
  FaMagnifyingGlass,
} from "react-icons/fa6";

export function Inventory() {
  const [isListView, setIsListView] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);
  const [showUnlock, setShowUnlock] = useState(false);
  const [activeLock, setActiveLock] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity] = useState<"success" | "info" | "warning" | "error">(
    "success"
  );
  const { t } = useTranslation();
  const { data: inventoryData, isLoading } = useGetLocksListQuery({
    page: currentPage,
    limit: itemsPerPage,
    imei: searchTerm,
  });

  const inventoryList = inventoryData?.response || [];
  const metaCount = inventoryData?.meta?.count || 0;
  const [unlockLock, result] = useUnlockLockMutation();

  const onChangeView = () => {
    setIsListView(!isListView);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const onUnlockConfirm = () => {
    unlockLock(activeLock)
      .unwrap()
      .then((response: any) => {
        setShowUnlock(false);
        setOpenSnackbar(true);
        setSnackbarMessage(response.message);
      })
      .catch((response) => {
      });
  };

  const onChangeSearch = (searchString: string) => {
    setSearchTerm(searchString); // Update the search term state
    setCurrentPage(1); // Reset to the first page
  };

  return (
    <>
      <div
        className="w-full h-screen overflow-y-auto px-8 pb-32"
        style={{ backgroundColor: "var(--bg-base)", color: "var(--text-main)" }}
      >
        <div
          className="sticky top-0 z-20 py-8 px-4"
          style={{ backgroundColor: "var(--bg-base)" }}
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h3 className="text-2xl">{t("nav_bar.inventory")}</h3>
              <p
                className="mt-1 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {isListView ? t("inventory.list_view") : t("inventory.card_view")}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-[320px]">
                <FaMagnifyingGlass
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                  style={{ color: "var(--text-muted)" }}
                />
                <input
                  type="text"
                  placeholder={t("inventory.search_placeholder")}
                  className="h-12 w-full rounded-lg border bg-[var(--bg-surface)] pl-11 pr-4 text-[var(--text-main)] outline-none transition-colors placeholder:text-[var(--text-muted)]"
                  style={{ borderColor: "var(--border-light)" }}
                  onChange={(e) => onChangeSearch(e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={onChangeView}
                className="flex h-12 w-12 items-center justify-center rounded-lg border transition-colors"
                style={{
                  backgroundColor: "var(--bg-surface)",
                  borderColor: "var(--border-light)",
                  color: "var(--text-muted)",
                }}
                aria-label={isListView ? t("inventory.card_view") : t("inventory.list_view")}
                title={isListView ? t("inventory.card_view") : t("inventory.list_view")}
              >
                {isListView ? <FaBorderAll className="text-lg" /> : <FaListUl className="text-lg" />}
              </button>
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div
              className="px-8 py-10 text-center"
            >
              <h3 className="mb-2 text-2xl tracking-wide">{t("inventory.loading")}</h3>
              <div className="mx-auto mt-3 h-1 w-24 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
            </div>
          </div>
        ) : inventoryList.length > 0 ? (
          <section
            className={`mt-2 pb-10 grid ${isListView ? "grid-cols-1" : "grid-cols-1 gap-6 xl:grid-cols-3 md:grid-cols-2"
              }`}
          >
            {isListView ? (
              <div
                className="overflow-hidden rounded-2xl border shadow-card"
                style={{
                  backgroundColor: "var(--bg-surface)",
                  borderColor: "var(--border-light)",
                }}
              >
                {inventoryList.map((inventoryItem: InventoryItem) => (
                  <InventoryList
                    key={inventoryItem.imei}
                    inventory={inventoryItem}
                    onUnlock={() => {
                      setShowUnlock(true);
                      setActiveLock(inventoryItem.imei);
                    }}
                  />
                ))}
              </div>
            ) : (
              inventoryList.map((inventoryItem: InventoryItem) => (
                <InventoryCard
                  key={inventoryItem.imei}
                  inventory={inventoryItem}
                  onUnlock={() => {
                    setShowUnlock(true);
                    setActiveLock(inventoryItem.imei);
                  }}
                />
              ))
            )}
          </section>
        ) : (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div
              className="rounded-2xl border px-8 py-10 text-center"
              style={{
                backgroundColor: "var(--bg-surface)",
                borderColor: "var(--border-light)",
              }}
            >
              <h3 className="mb-2 text-2xl tracking-wide">{t("inventory.no_locks_found")}</h3>
              <div className="mx-auto mt-3 h-1 w-24 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
            </div>
          </div>
        )}
        {inventoryList.length > 0 && (
          <div
            className="fixed bottom-0 left-0 right-0 z-10 flex justify-center border-t py-4"
            style={{
              backgroundColor: "var(--bg-base)",
              borderColor: "var(--border-light)",
            }}
          >
            <Pagination
              total={
                metaCount > 0
                  ? Math.ceil(metaCount / itemsPerPage)
                  : 0
              }
              onChange={handlePageChange}
              radius="full"
              key={`pagination-${currentPage}`}
              color="success"
              size="md"
              showShadow
              showControls={true}
              initialPage={1}
              page={currentPage}
              classNames={{
                next:
                  "rtl:rotate-180 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-main)]",
                prev:
                  "rtl:rotate-180 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-main)]",
                item:
                  "border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-main)]",
                cursor: "bg-[var(--color-primary)] text-[var(--text-invert)] shadow-[var(--shadow-card)]",
              }}
            />
          </div>
        )}
      </div>
      <Modal
        show={showUnlock}
        onClose={() => setShowUnlock(false)}
        align="center"
        className="top-10 left-1/2 mx-auto w-[92%] max-w-md -translate-x-1/2 transform rounded-2xl border bg-[var(--bg-surface)] text-[var(--text-main)] shadow-card"
      >
        <div className="p-6 text-center" style={{ color: "var(--text-main)" }}>
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{ backgroundColor: "var(--bg-surface-2)" }}
          >
            <FaLockOpen className="text-xl" style={{ color: "var(--color-primary)" }} />
          </div>
          <h3 className="text-xl font-semibold">{t("inventory.confirm_modal_title")}</h3>
          <p
            className="mb-6 mt-2 text-sm"
            style={{ color: "var(--text-muted)" }}
          >
            {t("inventory.confirm_desc")}
          </p>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <button
              className="rounded-lg border p-3 font-medium"
              style={{
                borderColor: "var(--border-light)",
                color: "var(--text-main)",
              }}
              onClick={() => setShowUnlock(false)}
            >
              {t("button.cancel")}
            </button>
            <button
              className="flex items-center justify-center rounded-lg p-3 font-semibold transition-colors duration-200"
              style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
              onClick={onUnlockConfirm}
              disabled={result.isLoading}
            >
              {result.isLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                t("button.unlock")
              )}
            </button>
          </div>
        </div>
      </Modal>

      <CustomSnackbar
        open={openSnackbar}
        duration={2000}
        handleClose={() => {
          setOpenSnackbar(false);
        }}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}
